import { useCallback, useEffect, useRef, useState } from "react";

import AppShell from "../components/AppShell";
import SharingTabs from "../components/SharingTabs";
import { CardSkeleton } from "../components/Skeleton";
import { getOfficeFiles, setOfficeFilesEnabled, type OfficeFilesOverview, type OfficeFilesPC } from "../api/officeFiles";
import { useAuth } from "../stores/auth";

const REFRESH_MS = 15_000;

type Tone = "good" | "busy" | "muted" | "bad";

export default function OfficeFiles() {
  const [data, setData] = useState<OfficeFilesOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const role = useAuth((s) => s.role);
  const canChange = role === "owner" || role === "admin";
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const d = await getOfficeFiles();
      if (alive.current) {
        setData(d);
        setError(null);
      }
    } catch (e) {
      if (alive.current) setError(extractError(e));
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void load();
    const t = window.setInterval(load, REFRESH_MS);
    return () => {
      alive.current = false;
      window.clearInterval(t);
    };
  }, [load]);

  async function toggle(pc: OfficeFilesPC) {
    setSaving(pc.agent_id);
    try {
      await setOfficeFilesEnabled(pc.agent_id, !pc.enabled);
      await load();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSaving(null);
    }
  }

  const server = data?.pcs.find((p) => p.is_server);
  const total = server?.status.global_bytes ?? 0;

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <h1 className="text-2xl font-semibold text-slate-900">Sharing</h1>
        <p className="mt-1 text-slate-600">Share folders, printers and scanners with everyone in the office.</p>
        <SharingTabs />

        <h2 className="text-lg font-semibold text-slate-900">KraetdNet Office Files</h2>
        <p className="mt-1 text-sm text-slate-600">
          Every computer has a <span className="font-medium">&ldquo;KraetdNet Office Files&rdquo;</span> shortcut
          on its desktop. Anything saved there shows up on all the other computers within moments. Each computer works
          on its own copy, so nothing freezes, even when another computer is switched off. Changes catch up when it
          comes back on.
        </p>
        <p className="mt-2 text-sm text-slate-600">
          Inside, each computer has its own folder, such as &ldquo;KraetdNet - Front Desk&rdquo;, so everyone can
          see where a file came from. Anyone can still open and edit everything.
        </p>
        <p className="mt-2 text-sm text-slate-600">
          Deleted or replaced files are kept for 30 days on the office server computer, in case you need them back.
        </p>

        {error ? (
          <p role="alert" className="mt-6 rounded-lg bg-red-50 p-3 text-sm text-red-800">
            {error}
          </p>
        ) : null}

        {data === null && !error ? (
          <div className="mt-6">
            <CardSkeleton />
          </div>
        ) : null}

        {data && !data.available ? (
          <div className="mt-6 rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-600">
            Office Files works with a KraetdNet office server.
          </div>
        ) : null}

        {data?.available && !data.has_server_pc ? (
          <div className="mt-6 rounded-xl border border-dashed border-slate-300 p-6 text-sm text-slate-600">
            <p className="font-medium text-slate-900">Waiting for the office server computer.</p>
            <p className="mt-1">
              Office Files starts once KraetdNet is running on the office server computer itself. Setting up the
              server does this by itself; if you skipped it, run kraetdnet-agent.exe from the server&rsquo;s
              client-package folder on that computer.
            </p>
          </div>
        ) : null}

        {data?.available && data.has_server_pc ? (
          <>
            {total > 0 ? (
              <p className="mt-4 text-sm text-slate-600">
                Office Files holds {formatBytes(total)} ({server?.status.global_files ?? 0} files).
              </p>
            ) : null}
            <ul className="mt-4 space-y-3">
              {data.pcs.map((pc) => {
                const s = describe(pc);
                return (
                  <li key={pc.agent_id} className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">
                          {pc.name}
                          {pc.is_server ? (
                            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                              Office server
                            </span>
                          ) : null}
                        </p>
                        <p className={`text-sm ${TONE[s.tone]}`} role="status">
                          {s.text}
                        </p>
                      </div>
                      {canChange && !pc.is_server ? (
                        <button
                          type="button"
                          onClick={() => void toggle(pc)}
                          disabled={saving !== null}
                          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 [-webkit-tap-highlight-color:transparent] hover:bg-slate-50 disabled:opacity-50"
                        >
                          {saving === pc.agent_id ? "Saving…" : pc.enabled ? "Turn off here" : "Turn on here"}
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
            {canChange ? (
              <p className="mt-4 text-xs text-slate-500">
                Turning Office Files off on a computer stops updating its copy. The files already on it stay there.
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </AppShell>
  );
}

const TONE: Record<Tone, string> = {
  good: "text-green-700",
  busy: "text-amber-700",
  muted: "text-slate-500",
  bad: "text-red-700",
};

function describe(pc: OfficeFilesPC): { text: string; tone: Tone } {
  const s = pc.status ?? {};
  if (!pc.enabled) return { text: "Off on this computer", tone: "muted" };
  if (!pc.supported) return { text: "Waiting for KraetdNet to update itself on this computer", tone: "muted" };
  if (!pc.online) return { text: "Computer is off. Its copy catches up when it's back on.", tone: "muted" };
  if (s.state === "error" || s.error) return { text: `Problem: ${s.error || "the folder can't be updated"}`, tone: "bad" };
  if (!s.running) return { text: "Setting up…", tone: "busy" };
  if (!pc.is_server && !s.peers_connected) return { text: "Trying to reach the office server…", tone: "busy" };
  if ((s.need_files ?? 0) > 0) {
    const n = s.need_files ?? 0;
    return { text: `Updating: ${n} file${n === 1 ? "" : "s"} to go (${formatBytes(s.need_bytes ?? 0)})`, tone: "busy" };
  }
  if (s.state && s.state !== "idle") return { text: "Checking for changes…", tone: "busy" };
  if ((s.errors ?? 0) > 0) {
    const n = s.errors ?? 0;
    return { text: `Up to date, except ${n} file${n === 1 ? "" : "s"} that couldn't be copied (maybe open in another program)`, tone: "bad" };
  }
  if (pc.is_server) {
    const n = s.peers_connected ?? 0;
    return { text: `Keeps the main copy. ${n} other computer${n === 1 ? " is" : "s are"} connected.`, tone: "good" };
  }
  return { text: "Up to date", tone: "good" };
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} bytes`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function extractError(e: unknown): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return "Something went wrong. Please try again.";
}
